import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Truck, Plus, Trash2, PencilLine, TestTube2, ExternalLink, CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin page — Courier tracking-URL templates.
 * Lets admins add/override the courier→tracking URL mapping used by the
 * "Sample dispatched to client" email. Rows here take precedence over the
 * built-in defaults which are displayed read-only at the bottom for reference.
 */
export default function CourierTemplates() {
  const [rows, setRows] = useState([]);
  const [builtin, setBuiltin] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ keywords: "", url_template: "", enabled: true, priority: 100, notes: "" });

  const [previewCourier, setPreviewCourier] = useState("");
  const [previewWaybill, setPreviewWaybill] = useState("");
  const [previewResult, setPreviewResult] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/courier-templates");
      setRows(data.rows || []);
      setBuiltin(data.builtin || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load courier templates");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ keywords: "", url_template: "https://tracking.example.com/track?awb={waybill}", enabled: true, priority: 100, notes: "" });
    setFormOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      keywords: (row.keywords || []).join(", "),
      url_template: row.url_template,
      enabled: row.enabled !== false,
      priority: row.priority ?? 100,
      notes: row.notes || "",
    });
    setFormOpen(true);
  };
  const submitForm = async () => {
    const kws = form.keywords.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (kws.length === 0) { toast.error("Enter at least one keyword (comma-separated)"); return; }
    if (!form.url_template.includes("{waybill}")) { toast.error("URL template must contain {waybill}"); return; }
    if (!/^https?:\/\//i.test(form.url_template)) { toast.error("URL must start with http:// or https://"); return; }
    const body = {
      keywords: kws,
      url_template: form.url_template.trim(),
      enabled: !!form.enabled,
      priority: Number(form.priority) || 100,
      notes: form.notes.trim(),
    };
    try {
      if (editing) {
        await api.put(`/admin/courier-templates/${editing.id}`, body);
        toast.success("Template updated");
      } else {
        await api.post("/admin/courier-templates", body);
        toast.success("Template added");
      }
      setFormOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };
  const deleteRow = async (row) => {
    if (!window.confirm(`Delete template for [${(row.keywords || []).join(", ")}]?`)) return;
    try {
      await api.delete(`/admin/courier-templates/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };
  const runPreview = async () => {
    if (!previewCourier.trim()) { toast.error("Courier name is required"); return; }
    setPreviewing(true);
    try {
      const { data } = await api.post("/admin/courier-templates/preview", {
        courier: previewCourier.trim(),
        waybill: previewWaybill.trim() || "TEST-WAYBILL-123",
      });
      setPreviewResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setPreviewing(false); }
  };

  return (
    <div className="space-y-6" data-testid="courier-templates-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[11.5px] font-mono tracking-widest text-brand uppercase inline-flex items-center gap-1.5"><Truck className="h-3.5 w-3.5" /> Admin</div>
          <h1 className="mt-1 font-heading font-bold text-3xl tracking-tight">Courier Tracking URLs</h1>
          <p className="text-slate-500 text-[13px] mt-1">Map free-text courier names to public tracking URLs. These are used to turn the waybill in the <b>Sample dispatched to client</b> email into a clickable link. Rows here override the shipped defaults — leave a courier out and the built-in fallback kicks in.</p>
        </div>
        <Button className="bg-brand hover:bg-brand-dark text-white" onClick={openCreate} data-testid="courier-add-btn"><Plus className="h-4 w-4 mr-2" />Add courier</Button>
      </div>

      {/* Preview panel */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-2"><TestTube2 className="h-4 w-4 text-brand" /><div className="font-heading font-semibold text-sm">Try it out</div></div>
        <div className="text-[12.5px] text-slate-500 mb-3">Quickly sanity-check any courier + waybill against the resolver — DB rows win first, then the built-in list.</div>
        <div className="flex flex-col md:flex-row md:items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Courier</label>
            <Input value={previewCourier} onChange={(e) => setPreviewCourier(e.target.value)} placeholder="e.g. DHL Express" data-testid="preview-courier-input" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Waybill (optional)</label>
            <Input value={previewWaybill} onChange={(e) => setPreviewWaybill(e.target.value)} placeholder="TEST-WAYBILL-123" data-testid="preview-waybill-input" />
          </div>
          <Button onClick={runPreview} disabled={previewing} variant="outline" data-testid="preview-run-btn">{previewing ? "Testing…" : "Test resolver"}</Button>
        </div>
        {previewResult && (
          <div className={`mt-3 text-[12.5px] rounded-md px-3 py-2 border ${previewResult.source === "none" ? "bg-rose-50 border-rose-200 text-rose-900" : previewResult.source === "db" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-slate-50 border-slate-200 text-slate-800"}`} data-testid="preview-result">
            {previewResult.source === "none" ? (
              <div className="flex items-center gap-1.5"><Info className="h-3.5 w-3.5" /> No template matched — the email will show the plain waybill.</div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> Matched via <b>{previewResult.source === "db" ? "admin-added row" : "built-in fallback"}</b> · keywords: <span className="font-mono">{previewResult.matched_keywords.join(", ")}</span></div>
                <a href={previewResult.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-brand underline break-all"><ExternalLink className="h-3 w-3" />{previewResult.url}</a>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Custom rows */}
      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center gap-2">
          <div className="font-heading font-semibold text-sm">Custom courier rows</div>
          <span className="ml-1 text-[11px] text-slate-500 font-mono">({rows.length})</span>
        </div>
        {loading ? (
          <div className="p-4 text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-slate-500 text-center" data-testid="courier-rows-empty">No custom rows yet — the built-in list below is used as-is. Click <b>Add courier</b> to override a default or introduce a new one.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="courier-rows-table">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2">Priority</th>
                  <th className="text-left px-3 py-2">Keywords</th>
                  <th className="text-left px-3 py-2">URL template</th>
                  <th className="text-left px-3 py-2">Enabled</th>
                  <th className="text-left px-3 py-2">Notes</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`courier-row-${r.id}`}>
                    <td className="px-3 py-2 tabular-nums font-mono text-slate-600">{r.priority ?? 100}</td>
                    <td className="px-3 py-2 font-mono text-slate-800">{(r.keywords || []).join(", ")}</td>
                    <td className="px-3 py-2 font-mono text-[11.5px] text-slate-600 break-all">{r.url_template}</td>
                    <td className="px-3 py-2 text-[12px]">{r.enabled === false ? <span className="text-slate-400">Off</span> : <span className="text-emerald-700 font-semibold">On</span>}</td>
                    <td className="px-3 py-2 text-slate-500 text-[12px]">{r.notes || ""}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap space-x-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`courier-edit-${r.id}`}><PencilLine className="h-3.5 w-3.5 mr-1" />Edit</Button>
                      <Button size="sm" variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => deleteRow(r)} data-testid={`courier-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />Delete</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Built-in fallback list */}
      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center gap-2">
          <div className="font-heading font-semibold text-sm">Built-in fallback list</div>
          <span className="ml-1 text-[11px] text-slate-500 font-mono">({builtin.length})</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Keywords</th>
                <th className="text-left px-3 py-2">URL template</th>
              </tr>
            </thead>
            <tbody>
              {builtin.map((b, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono text-slate-800">{b.keywords.join(", ")}</td>
                  <td className="px-3 py-2 font-mono text-[11.5px] text-slate-600 break-all">{b.url_template}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent data-testid="courier-form-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Truck className="h-5 w-5 text-brand" />{editing ? "Edit courier template" : "Add courier template"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Keywords (comma-separated) *</label>
              <Input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="e.g. dhl, dhl express" data-testid="courier-form-keywords" />
              <div className="text-[11px] text-slate-500 mt-1">Case-insensitive substring match against the courier field entered at dispatch. Any one match wins.</div>
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">URL template *</label>
              <Input value={form.url_template} onChange={(e) => setForm({ ...form, url_template: e.target.value })} placeholder="https://tracking.example.com/track?awb={waybill}" data-testid="courier-form-url" className="font-mono" />
              <div className="text-[11px] text-slate-500 mt-1">Must contain the <span className="font-mono">{"{waybill}"}</span> placeholder. Waybill is URL-encoded at render time.</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Priority</label>
                <Input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="courier-form-priority" />
                <div className="text-[11px] text-slate-500 mt-1">Lower = evaluated first. Default 100.</div>
              </div>
              <div className="flex items-end">
                <label className="inline-flex items-center gap-2 text-[13px] text-slate-700">
                  <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="courier-form-enabled" />
                  <span>Enabled</span>
                </label>
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-600">Notes (optional)</label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="courier-form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button className="bg-brand hover:bg-brand-dark text-white" onClick={submitForm} data-testid="courier-form-submit">{editing ? "Save changes" : "Add courier"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
